import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Card,
  Row,
  Col,
  Alert,
  DatePicker,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { Payment, PaymentFilters, paymentAPI, PaymentStatistics } from '../../services/paymentAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import PaymentForm from './PaymentForm';
import dayjs from 'dayjs';

const { Search } = Input;
const { RangePicker } = DatePicker;

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface PaymentListProps {
  selectedCompany: SelectedCompany | null;
}

const PaymentList: React.FC<PaymentListProps> = ({ selectedCompany }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [filters, setFilters] = useState<PaymentFilters>({});
  const [statistics, setStatistics] = useState<PaymentStatistics | null>(null);

  const paymentStatusLabels = {
    'gözləyir': 'Gözləyir',
    'tam_ödənilməyib': 'Tam ödənilməyib',
    'tam_ödənilib': 'Tam ödənilib',
    'gecikir': 'Gecikir',
    'ləğv_edilib': 'Ləğv edilib'
  };

  const paymentMethodLabels = {
    'nəğd': 'Nəğd',
    'kart': 'Bank kartı',
    'köçürmə': 'Bank köçürməsi',
    'online': 'Online ödəniş'
  };

  useEffect(() => {
    if (selectedCompany) {
      loadPayments();
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCompany]);

  const loadPayments = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const companyFilters = { ...filters, company: selectedCompany.id };
      const response = await paymentAPI.getPayments(companyFilters);
      
      if (response.success && response.data) {
        setPayments(response.data);
      } else {
        message.error(response.message || 'Ödənişlər yüklənmədi');
      }
    } catch (error) {
      message.error('Server xətası');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await paymentAPI.getStatistics(selectedCompany.id);
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Statistikalar yüklənmədi:', error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await paymentAPI.deletePayment(id);
      if (response.success) {
        message.success(response.message || 'Ödəniş silindi');
        loadPayments();
        loadStatistics();
      } else {
        message.error(response.message || 'Ödəniş silinmədi');
      }
    } catch (error) {
      message.error('Server xətası');
    }
  };

  const handleEdit = (payment: Payment) => {
    setEditingPayment(payment);
    setIsModalVisible(true);
  };

  const handleAdd = () => {
    setEditingPayment(null);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingPayment(null);
  };

  const handleFormSuccess = () => {
    loadPayments();
    loadStatistics();
    handleModalClose();
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters({
        ...filters,
        date_from: dates[0].format('YYYY-MM-DD'),
        date_to: dates[1].format('YYYY-MM-DD')
      });
    } else {
      const { date_from, date_to, ...otherFilters } = filters;
      setFilters(otherFilters);
    }
  };

  const columns = [
    {
      title: 'Tələbə',
      key: 'student',
      render: (record: Payment) => (
        <div>
          <div className="font-medium">{record.student_name}</div>
          <div className="text-gray-500 text-sm">FIN: {record.student_fin}</div>
          <div className="text-blue-600 text-sm">{record.course_name}</div>
        </div>
      ),
    },
    {
      title: 'Dövr',
      key: 'period',
      render: (record: Payment) => (
        <div>
          <div className="font-medium">{record.period_display}</div>
          <div className="text-gray-500 text-sm">
            {record.period_year}/{record.period_month}
          </div>
        </div>
      ),
    },
    {
      title: 'Məbləğ',
      key: 'amount',
      render: (record: Payment) => (
        <div>
          <div className="font-semibold text-green-600">
            {record.paid_amount} / {record.required_amount} AZN
          </div>
          {record.debt_amount > 0 && (
            <div className="text-red-600 text-sm">
              Borc: {record.debt_amount} AZN
            </div>
          )}
          {record.remaining_amount && record.remaining_amount > 0 && (
            <div className="text-orange-600 text-sm">
              Qalan: {record.remaining_amount} AZN
            </div>
          )}
        </div>
      ),
      sorter: (a: Payment, b: Payment) => a.required_amount - b.required_amount,
    },
    {
      title: 'Son ödəniş tarixi',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date: string, record: Payment) => (
        <div>
          <div>{dayjs(date).format('DD.MM.YYYY')}</div>
          {record.is_overdue && (
            <Tag color="red">
              {record.days_overdue} gün gecikir
            </Tag>
          )}
        </div>
      ),
      sorter: (a: Payment, b: Payment) => dayjs(a.due_date).unix() - dayjs(b.due_date).unix(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors = {
          'gözləyir': 'orange',
          'tam_ödənilməyib': 'blue',
          'tam_ödənilib': 'green',
          'gecikir': 'red',
          'ləğv_edilib': 'gray'
        };
        return (
          <Tag color={colors[status as keyof typeof colors]}>
            {paymentStatusLabels[status as keyof typeof paymentStatusLabels]}
          </Tag>
        );
      },
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      width: 120,
      render: (record: Payment) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            title="Ödəniş əlavə et"
          />
          <Popconfirm
            title="Ödənişi silmək istədiyinizə əminsiniz?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Bəli"
            cancelText="Xeyr"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              title="Sil"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Şirkət seçilmədiyi halda xəbərdarlıq */}
      {!selectedCompany && (
        <Alert
          message="Şirkət seçilməyib"
          description="Ödənişləri görmək üçün yuxarıdan şirkət seçin."
          type="warning"
          showIcon
          className="mb-6"
        />
      )}

      {selectedCompany && (
        <>
          {/* Statistikalar */}
          {statistics && (
            <Row gutter={16} className="mb-6">
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Ümumi ödənişlər"
                    value={statistics.total_payments}
                    prefix={<DollarOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Tam ödənilmiş"
                    value={statistics.paid_payments}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Tam ödənilməmiş"
                    value={statistics.unpaid_payments}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Filtrlər və Əlavə et düyməsi */}
          <Card className="mb-4">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={6}>
                <Search
                  placeholder="Ödəniş axtar..."
                  allowClear
                  onSearch={(value) => setFilters({ ...filters, search: value })}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Select
                  placeholder="Status"
                  allowClear
                  style={{ width: '100%' }}
                  onChange={(value) => setFilters({ ...filters, payment_status: value })}
                  options={[
                    { value: 'tam_ödənilməyib', label: 'Tam ödənilməyib' },
                    { value: 'tam_ödənilib', label: 'Tam ödənilib' }
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Başlanğıc tarix', 'Son tarix']}
                  onChange={handleDateRangeChange}
                />
              </Col>
              <Col xs={24} sm={12} md={4} className="text-right">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAdd}
                  size="large"
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  Ödəniş Əlavə Et
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Ödənişlər cədvəli */}
          <Card>
            <Table
              columns={columns}
              dataSource={payments}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ödəniş`,
              }}
              rowClassName={(record) => record.is_overdue ? 'bg-red-50' : ''}
            />
          </Card>

          {/* Ödəniş əlavə/redaktə formu */}
          <PaymentForm
            visible={isModalVisible}
            payment={editingPayment}
            onCancel={handleModalClose}
            onSuccess={handleFormSuccess}
            selectedCompany={selectedCompany}
          />
        </>
      )}
    </div>
  );
};

export default PaymentList;